/**
 * Sorts current addresses to front to match results-card formatter.
 *
 * @param {object} left
 * @param {object} right
 * @returns {number}
 */
const compareCurrentAddressFirst = (left = {}, right = {}) => {
  if (left.current_address && !right.current_address) {
    return -1;
  }

  if (!left.current_address && right.current_address) {
    return 1;
  }

  return 0;
};

/**
 * Normalizes raw results address into minimal card-compatible shape.
 *
 * @param {object} address
 * @returns {object}
 */
const normalizeResultAddress = (address = {}) => {
  return {
    current_address: Boolean(address.current_address),
    street_number:
      typeof address.street_number === 'undefined' ? '' : address.street_number,
    street_name: address.street_name ?? '',
    street_type: address.street_type ?? '',
    street_affix: address.street_affix ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zip: address.zip ?? ''
  };
};

/**
 * Returns whether address can contribute visible content to result card.
 *
 * @param {object} address
 * @returns {boolean}
 */
const isRenderableResultAddress = (address = {}) => {
  return !(
    address.street_number === 0 ||
    address.state === 'NULL' ||
    address.city === 'NULL'
  );
};

/**
 * Keeps only raw address rows needed to reproduce results-card output.
 *
 * @param {Array<object>} addresses
 * @param {object} opts
 * @param {number} opts.maxLocations
 * @param {number} opts.maxFullAddresses
 * @returns {Array<object>}
 */
const trimResultAddresses = (addresses, opts) => {
  if (!Array.isArray(addresses) || !addresses.length) {
    return [];
  }

  const { maxLocations, maxFullAddresses } = opts;
  const sortedAddresses = [...addresses].sort(compareCurrentAddressFirst);
  const trimmedAddresses = sortedAddresses
    .slice(0, maxFullAddresses)
    .map(normalizeResultAddress);
  const locationsDisplayed = [];

  for (
    let index = 0;
    index < Math.min(sortedAddresses.length, maxFullAddresses);
    index += 1
  ) {
    const address = sortedAddresses[index];

    if (!isRenderableResultAddress(address)) {
      continue;
    }

    locationsDisplayed.push(`${address.city ?? ''} ${address.state ?? ''}`);
  }

  for (
    let index = maxFullAddresses;
    index < sortedAddresses.length;
    index += 1
  ) {
    if (locationsDisplayed.length >= maxLocations) {
      break;
    }

    const address = sortedAddresses[index];

    if (!isRenderableResultAddress(address)) {
      continue;
    }

    const locationString = `${address.city ?? ''} ${address.state ?? ''}`;

    if (locationsDisplayed.includes(locationString)) {
      continue;
    }

    locationsDisplayed.push(locationString);
    trimmedAddresses.push(normalizeResultAddress(address));
  }

  return trimmedAddresses;
};

/**
 * Trims relatives to subset rendered in result card.
 *
 * @param {Array<object>} relatives
 * @param {number} maxRelatives
 * @returns {Array<object>}
 */
const trimResultRelatives = (relatives, maxRelatives) => {
  if (!Array.isArray(relatives) || !relatives.length) {
    return [];
  }

  return relatives
    .filter(relative => relative?.firstname && relative?.lastname)
    .slice(0, maxRelatives)
    .map(relative => {
      return {
        firstname: relative.firstname ?? '',
        lastname: relative.lastname ?? '',
        city: relative.city ?? '',
        state: relative.state ?? '',
        id: relative.id ?? null,
        unlink: Boolean(relative.unlink)
      };
    });
};

/**
 * Trims phones to minimal card-compatible shape.
 *
 * @param {Array<object>} phones
 * @param {number} maxPhones
 * @returns {Array<object>}
 */
const trimResultPhones = (phones, maxPhones) => {
  if (!Array.isArray(phones) || !phones.length) {
    return [];
  }

  return phones.slice(0, maxPhones).map(phone => {
    return {
      ...phone,
      unlink: Boolean(phone?.unlink)
    };
  });
};

/**
 * Trims emails to minimal card-compatible shape.
 *
 * @param {Array<object>} emails
 * @param {number} maxEmails
 * @returns {Array<object>}
 */
const trimResultEmails = (emails, maxEmails) => {
  if (!Array.isArray(emails) || !emails.length) {
    return [];
  }

  return emails.slice(0, maxEmails).map(email => {
    return {
      user: email?.user ?? '',
      domain: email?.domain ?? '',
      affix: email?.affix ?? '',
      full: email?.full ?? ''
    };
  });
};

/**
 * Preserves social-media count with tiny placeholder array so existing result
 * cards can keep the "profiles found" UI without hydrating full objects.
 *
 * @param {object} person
 * @returns {Array<string>}
 */
const trimSocialMedia = person => {
  const socialCount = Number.isFinite(person?.has_social_media)
    ? person.has_social_media
    : Array.isArray(person?.social_media)
    ? person.social_media.length
    : 0;

  return socialCount > 0 ? Array(socialCount).fill('') : [];
};

/**
 * Trims SEO results `people` array to data required for hydrated person cards,
 * sort/filter UI, and CTA/profile links.
 *
 * @param {Array<object>} people
 * @param {object} [opts]
 * @param {number} [opts.maxAliases=5]
 * @param {number} [opts.maxPhones=5]
 * @param {number} [opts.maxEmails=5]
 * @param {number} [opts.maxRelatives=5]
 * @param {number} [opts.maxLocations=5]
 * @param {number} [opts.maxFullAddresses=2]
 * @returns {Array<object>}
 */
const trimSeoResultsPeople = (people, opts = {}) => {
  if (!Array.isArray(people) || !people.length) {
    return [];
  }

  const settings = {
    maxAliases: 5,
    maxPhones: 5,
    maxEmails: 5,
    maxRelatives: 5,
    maxLocations: 5,
    maxFullAddresses: 2,
    ...opts
  };

  return people.map(person => {
    return {
      id: person?.id ?? null,
      cid: person?.cid ?? null,
      firstname: person?.firstname ?? '',
      middlename: person?.middlename ?? '',
      lastname: person?.lastname ?? '',
      city: person?.city ?? '',
      state: person?.state ?? '',
      age: Number.isFinite(person?.age) ? person.age : null,
      dob: person?.dob ?? '',
      has_emails: Number.isFinite(person?.has_emails) ? person.has_emails : 0,
      has_phones: Number.isFinite(person?.has_phones) ? person.has_phones : 0,
      has_social_media: Number.isFinite(person?.has_social_media)
        ? person.has_social_media
        : 0,
      social_media: trimSocialMedia(person),
      aliases: Array.isArray(person?.aliases)
        ? person.aliases.slice(0, settings.maxAliases)
        : [],
      phones: trimResultPhones(person?.phones, settings.maxPhones),
      emails: trimResultEmails(person?.emails, settings.maxEmails),
      relatives: trimResultRelatives(person?.relatives, settings.maxRelatives),
      addresses: trimResultAddresses(person?.addresses, settings)
    };
  });
};

export default trimSeoResultsPeople;
